import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef
} from "react";
import { useTranslation } from "utilities/i18n";
import { infiniteNestLink, Switch } from "polyrouter";
import { Layout } from "views/Main/Layouts";
import {
  defaultTrialUserListState,
  TrialUserListReducer,
  updateDataLoaded,
  updatePageInfo,
  updateSearch,
  updateSelectedItems,
  updateSort
} from "./TrialUserListReducer";
import { TABLE_COLUMN_STATE, TABLES } from "store/userMeta";
import { Loader, NoResultsIndicatorAnimation } from "elements";
import { toLicenseDateLocaleDateString } from "utilities/licenseServerUtils";
import {
  getColumns,
  HEADER_ID,
  itemCheckedWithSet,
  sortTableData,
  TableBody,
  TableBulkItemHeader,
  TableBulkItemRow,
  TableHeaderItem,
  TableSearchEdit,
  toggleAll,
  toggleItemWithSet
} from "collections/Table";
import { TrialUser, useGetTrialUserList } from "../useTrialUsers";
import { useSelector } from "react-redux";
import { State } from "store";
import { SortState } from "store/filters";
import { ProtectedLink, ProtectedRoute } from "TsRoleGuards";
import { generateTableHeaders } from "utilities/tableUtils";
import { DeleteTrialUserModal } from "../Delete/DeleteTrialUserModal";
import { handleNullFields } from "utilities/stringUtils";

type TrialUserRowRowProps = {
  trialUser: TrialUser;
  trialUserVisible: boolean;
  onCheck: (id: string) => void;
  checked: boolean;
  showChecks: boolean;
  shownColumns: Array<string>;
};

export const TrialUserRow: React.FC<TrialUserRowRowProps> = ({
  trialUser,
  onCheck,
  checked,
  showChecks,
  shownColumns
}) => {
  const {
    id,
    email,
    meetingId,
    creationTime,
    expired,
    numberOfCalls,
    totalDurationMinutes,
    totalNumberOfParticipants,
    minNumberOfParticipants,
    maxNumberOfParticipants,
    averageNumberOfParticipants
  } = trialUser;
  const { t } = useTranslation();
  const { COLUMNS } = TABLES.CLARITI_TRIAL_USERS;

  const getTableContent = (column: string) => {
    let content: ReactNode = null;
    switch (column) {
      case COLUMNS.EMAIL:
        content = handleNullFields(email);
        break;
      case COLUMNS.CONFERENCE_ROOM:
        content = meetingId;
        break;
      case COLUMNS.DATE_CREATED:
        content = toLicenseDateLocaleDateString(creationTime);
        break;
      case COLUMNS.EXPIRED:
        const trialExpired = t(expired ? "YES" : "NO");
        content = trialExpired;
        break;
      case COLUMNS.TOTAL_CALLS:
        content = numberOfCalls;
        break;
      case COLUMNS.TOTAL_PARTICIPANTS:
        content = totalNumberOfParticipants;
        break;
      case COLUMNS.MIN_PARTICIPANTS:
        content = minNumberOfParticipants;
        break;
      case COLUMNS.MAX_PARTICIPANTS:
        content = maxNumberOfParticipants;
        break;
      case COLUMNS.AVERAGE_PARTICIPANTS:
        content = averageNumberOfParticipants.toFixed(3);
        break;
      case COLUMNS.TOTAL_DURATION:
        content = totalDurationMinutes.toFixed(2);
        break;
    }
    return <td key={column}>{content}</td>;
  };

  return (
    <TableBulkItemRow
      checked={checked}
      onCheck={() => onCheck(id)}
      showChecks={showChecks}
    >
      {shownColumns.map(getTableContent)}
    </TableBulkItemRow>
  );
};

export const TrialUserList: React.FC<{
  tenantId: string;
}> = ({ tenantId }) => {
  const { Content } = Layout;
  const { t } = useTranslation();
  const firstUpdate = useRef(true);
  const [
    {
      dataLoaded,
      selectedItems,
      sort,
      searchTerm,
      trialUserCount,
      trialUsers,
      visibleTrialUsers
    },
    dispatch
  ] = useReducer(TrialUserListReducer, defaultTrialUserListState);

  const { NAME: TABLE_NAME, COLUMNS } = TABLES.CLARITI_TRIAL_USERS;

  const {
    [TABLE_COLUMN_STATE.SHOWN]: shownColumns,
    [TABLE_COLUMN_STATE.HIDDEN]: hiddenColumns
  } = useSelector((state: State) => {
    return state.userMeta?.tables[TABLE_NAME] ?? {};
  });

  const { data, fetching, stale } = useGetTrialUserList({
    variables: {
      params: { limit: -1 }
    }
  });

  const formattedSearchTerm = searchTerm?.toLowerCase() ?? undefined;

  // Since filtering is handled locally, we will automatically load all data so things work as expected
  const filteredData = !!formattedSearchTerm;

  // Tenant ID switch
  useEffect(() => {
    // TODO:  If trial user subscription ever becomes tenant based, then reset dataLoaded to false is required
    // dispatch(updateDataLoaded(false));
    dispatch(updateSelectedItems([]));
  }, [tenantId]);

  const loading = fetching || stale;
  const trialUsersData = data;

  // refresh the display after the data is loaded
  useEffect(() => {
    const isFirstUpdate = firstUpdate.current;
    if (isFirstUpdate) {
      firstUpdate.current = false;
    }

    if (!loading) {
      if (trialUsersData && trialUsersData.edges) {
        dispatch(
          updatePageInfo({
            trialUserCount: trialUsersData.totalCount,
            trialUsers: trialUsersData.edges.map(entry => {
              return { ...entry.node };
            })
          })
        );
      }

      if (!isFirstUpdate) {
        dispatch(updateDataLoaded(true));
      }
    }
  }, [loading, trialUsersData]);

  const setSelectedItems = useCallback((items: Array<string>) => {
    dispatch(updateSelectedItems(items));
  }, []);

  const setSort = useCallback((sort: SortState) => {
    dispatch(updateSort(sort));
  }, []);

  const setSearch = useCallback((search?: string) => {
    dispatch(updateSearch(search));
  }, []);

  // Sort trialUsers
  sortTableData(trialUsers, sort);

  const toggleItem = toggleItemWithSet(
    trialUsers.length,
    selectedItems,
    setSelectedItems
  );

  const itemChecked = itemCheckedWithSet(selectedItems);

  // Aggregate and optimize selection state information
  const selectedTrialUsers = useMemo(
    () =>
      selectedItems.reduce((trialUserList, id) => {
        const trialUserObj = trialUsers.find(trialUser => trialUser.id === id);
        if (trialUserObj) {
          trialUserList.push(trialUserObj);
        }
        return trialUserList;
      }, [] as Array<TrialUser>),
    [selectedItems, trialUsers]
  );

  const columnTranslations = {
    [COLUMNS.EMAIL]: t("EMAIL"),
    [COLUMNS.CONFERENCE_ROOM]: t("CONFERENCE_ROOM"),
    [COLUMNS.DATE_CREATED]: t("DATE_CREATED"),
    [COLUMNS.EMAIL]: t("EMAIL"),
    [COLUMNS.EXPIRED]: t("EXPIRED"),
    [COLUMNS.TOTAL_CALLS]: t("TOTAL_CALLS"),
    [COLUMNS.TOTAL_PARTICIPANTS]: t("TOTAL_PARTICIPANTS"),
    [COLUMNS.MIN_PARTICIPANTS]: t("MIN_PARTICIPANTS"),
    [COLUMNS.MAX_PARTICIPANTS]: t("MAX_PARTICIPANTS"),
    [COLUMNS.AVERAGE_PARTICIPANTS]: t("AVERAGE_PARTICIPANTS"),
    [COLUMNS.TOTAL_DURATION]: t("TOTAL_DURATION_MINUTES")
  };

  const getTableHeaders = (column: string, title: string) => {
    return (
      <TableHeaderItem
        fieldName={column}
        title={title}
        sort={{
          currentSort: sort,
          onSort: setSort
        }}
        key={column}
      />
    );
  };

  const shown = getColumns(shownColumns, columnTranslations);
  const hidden = getColumns(hiddenColumns, columnTranslations);

  const refresh = () => {
    dispatch(updateSelectedItems([]));
  };

  const filteredTrialUsers = useMemo(() => {
    return formattedSearchTerm
      ? trialUsers.filter(
          trialUser =>
            trialUser?.firstName?.toLowerCase().includes(formattedSearchTerm) ||
            trialUser?.lastName?.toLowerCase().includes(formattedSearchTerm) ||
            trialUser?.email?.toLowerCase().includes(formattedSearchTerm)
        )
      : trialUsers;
  }, [formattedSearchTerm, trialUsers]);

  const hasSelectedItems = selectedItems.length > 0;

  const hasSingleSelectedIem = selectedItems.length === 1;

  // need to wire me up once backend supports the ability to edit subscriptions
  const canEditTrialUser = hasSingleSelectedIem && false;

  const displayCount = filteredData
    ? filteredTrialUsers.length
    : trialUserCount;

  return (
    <Content>
      <Loader loading={!dataLoaded}>
        {!loading && trialUserCount === 0 ? (
          <NoResultsIndicatorAnimation title={t("NO_TRIAL_USERS_FOUND")} />
        ) : (
          <>
            <div className="table-heading">
              <span className="metric" data-testid="trial-user-count">
                {displayCount}
              </span>
              <span className="title ml-5">
                {t("TRIAL_USER", { count: displayCount })}
              </span>
            </div>
            <div className="grouping mb-15 table-actions">
              <ProtectedLink
                to={canEditTrialUser ? infiniteNestLink("/edit") : ""}
                className={
                  canEditTrialUser
                    ? "btn btn-primary"
                    : "btn btn-primary disabled"
                }
                requirement="clariti-trial:UpdateSubscriptions"
              >
                {t("EDIT", {
                  count: selectedTrialUsers.length
                })}
              </ProtectedLink>
              <ProtectedLink
                to={hasSelectedItems ? infiniteNestLink("/remove") : ""}
                className={hasSelectedItems ? "btn" : "btn disabled"}
                requirement="clariti-trial:DeleteSubscriptions"
              >
                {t("REMOVE", {
                  count: selectedTrialUsers.length
                })}
              </ProtectedLink>
              <TableSearchEdit
                search={{
                  onSearch: setSearch,
                  searchPlaceholder: t("SEARCH_TRIAL_USERS")
                }}
                edit={{
                  tableName: TABLE_NAME,
                  shownColumns: shown,
                  hiddenColumns: hidden
                }}
              />
            </div>
            <div className="table-container">
              <table className="table custom-table">
                <TableBulkItemHeader
                  checked={itemChecked(HEADER_ID)}
                  onCheck={() =>
                    toggleAll(
                      trialUsers.map(trialUser => trialUser.id),
                      selectedItems,
                      setSelectedItems
                    )
                  }
                  showChecks={!!shownColumns.length}
                >
                  {generateTableHeaders(shown, getTableHeaders)}
                </TableBulkItemHeader>
                <TableBody
                  loading={loading}
                  cols={shown.length}
                  accommodateScrollbar
                >
                  {filteredTrialUsers.map(trialUser => {
                    return (
                      <TrialUserRow
                        key={trialUser.id}
                        trialUser={trialUser}
                        trialUserVisible={visibleTrialUsers.has(trialUser.id)}
                        onCheck={() => toggleItem(trialUser.id)}
                        checked={itemChecked(trialUser.id)}
                        showChecks={!!shownColumns.length}
                        shownColumns={shown.map(column => column.key)}
                      />
                    );
                  })}
                </TableBody>
              </table>
            </div>
            <Switch nest>
              <ProtectedRoute
                nest
                path="/edit"
                requirement="clariti-trial:UpdateSubscriptions"
              ></ProtectedRoute>
              <ProtectedRoute
                nest
                path="/remove"
                requirement="clariti-trial:DeleteSubscriptions"
              >
                <DeleteTrialUserModal
                  trialUsers={selectedTrialUsers}
                  refresh={refresh}
                />
              </ProtectedRoute>
            </Switch>
          </>
        )}
      </Loader>
    </Content>
  );
};
